import { useState, useEffect, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v3";
import {
  Loader2,
  Link2,
  AlertCircle,
  Type,
  AlignLeft,
  Plus,
  List,
  CheckCircle2,
  XCircle,
  ExternalLink,
  BookOpen,
  Globe,
  Layers,
} from "lucide-react";
import type { ModalComponentProps } from "@/lib/modal";
import { Button } from "@/components/ui/button";
import {
  ResponsiveModalDescription,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from "@/components/ui/responsive-modal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  useCreateDocument,
  useScrapeUrl,
  useBulkScrapeUrls,
  useScrapingStatus,
  useOfficialPresets,
  useApplyOfficialPreset,
  useDiscoverUrls,
  type BulkScrapeResult,
} from "@/lib/api/knowledge-base";
import type { OfficialDocumentPreset } from "@/lib/api/types";
import { useSubscriptionError } from "@/hooks/use-subscription-error";
import { useGuildWS } from "@/hooks/use-websocket";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import type { WSServerMessage } from "@discord-bldr/shared";

const manualFormSchema = z.object({
  title: z.string().max(200, "Title must be 200 characters or less").optional(),
  content: z.string().min(1, "Content is required"),
});

const urlFormSchema = z.object({
  url: z.string().url("Please enter a valid URL"),
});

const bulkUrlFormSchema = z.object({
  urls: z.string().min(1, "Enter at least one URL"),
});

type ManualFormValues = z.infer<typeof manualFormSchema>;
type UrlFormValues = z.infer<typeof urlFormSchema>;
type BulkUrlFormValues = z.infer<typeof bulkUrlFormSchema>;

function parseUrls(raw: string): string[] {
  return raw
    .split(/[\n,]+/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

export interface AddDocumentModalProps {
  applicationGuildId: string;
  tenantId?: string;
}

export function AddDocumentModal({
  applicationGuildId,
  tenantId,
  onClose,
}: AddDocumentModalProps & ModalComponentProps) {
  const createMutation = useCreateDocument(applicationGuildId);
  const scrapeMutation = useScrapeUrl(applicationGuildId);
  const bulkScrapeMutation = useBulkScrapeUrls(applicationGuildId);
  const { data: scrapingStatus } = useScrapingStatus(applicationGuildId);
  const { handleError } = useSubscriptionError({
    tenantId: tenantId || "",
    fallbackMessage: "Failed to add document",
  });

  const [bulkResults, setBulkResults] = useState<BulkScrapeResult[] | null>(
    null
  );

  const manualForm = useForm<ManualFormValues>({
    resolver: zodResolver(manualFormSchema),
    defaultValues: {
      title: "",
      content: "",
    },
  });

  const urlForm = useForm<UrlFormValues>({
    resolver: zodResolver(urlFormSchema),
    defaultValues: {
      url: "",
    },
  });

  const bulkUrlForm = useForm<BulkUrlFormValues>({
    resolver: zodResolver(bulkUrlFormSchema),
    defaultValues: {
      urls: "",
    },
  });

  const isScrapingAvailable = scrapingStatus?.data?.isAvailable ?? false;

  const onManualSubmit = async (values: ManualFormValues) => {
    try {
      await createMutation.mutateAsync({
        content: values.content.trim(),
        title: values.title?.trim() || undefined,
        sourceType: "direct",
      });

      onClose();
    } catch (error) {
      handleError(error);
    }
  };

  const onUrlSubmit = async (values: UrlFormValues) => {
    try {
      await scrapeMutation.mutateAsync(values.url.trim());

      onClose();
    } catch (error) {
      handleError(error);
    }
  };

  const onBulkUrlSubmit = async (values: BulkUrlFormValues) => {
    const urls = parseUrls(values.urls);
    if (urls.length === 0) return;

    if (urls.length > 25) {
      bulkUrlForm.setError("urls", {
        message: "Maximum 25 URLs at a time",
      });
      return;
    }

    const invalidUrls = urls.filter((url) => {
      try {
        new URL(url);
        return false;
      } catch {
        return true;
      }
    });

    if (invalidUrls.length > 0) {
      bulkUrlForm.setError("urls", {
        message: `Invalid URL${invalidUrls.length > 1 ? "s" : ""}: ${invalidUrls.slice(0, 3).join(", ")}${invalidUrls.length > 3 ? ` and ${invalidUrls.length - 3} more` : ""}`,
      });
      return;
    }

    try {
      const result = await bulkScrapeMutation.mutateAsync(urls);
      setBulkResults(result.data.results);
    } catch (error) {
      handleError(error);
    }
  };

  const urlCount = parseUrls(bulkUrlForm.watch("urls")).length;

  return (
    <div className="space-y-4">
      <ResponsiveModalHeader>
        <div className="flex items-center gap-3 text-left">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-rose-500/10 to-red-500/10">
            <Plus className="size-5 text-rose-500" />
          </div>
          <div>
            <ResponsiveModalTitle>Add Document</ResponsiveModalTitle>
            <ResponsiveModalDescription className="mt-1">
              Add content to the knowledge base manually or from a URL.
            </ResponsiveModalDescription>
          </div>
        </div>
      </ResponsiveModalHeader>

      <Tabs defaultValue="manual">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="manual">
            <AlignLeft className="mr-1.5 size-3.5" />
            Manual
          </TabsTrigger>
          <TabsTrigger value="url">
            <Link2 className="mr-1.5 size-3.5" />
            URL
          </TabsTrigger>
          <TabsTrigger value="bulk">
            <List className="mr-1.5 size-3.5" />
            Bulk
          </TabsTrigger>
          <TabsTrigger value="crawl">
            <Globe className="mr-1.5 size-3.5" />
            Crawl
          </TabsTrigger>
          <TabsTrigger value="official">
            <BookOpen className="mr-1.5 size-3.5" />
            Official
          </TabsTrigger>
        </TabsList>

        <TabsContent value="manual" className="mt-4">
          <Form {...manualForm}>
            <form onSubmit={manualForm.handleSubmit(onManualSubmit)}>
              <div className="grid gap-4">
                <FormField
                  control={manualForm.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Type className="size-4 text-rose-500" />
                        Title (optional)
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g., API Documentation Overview"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={manualForm.control}
                  name="content"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <AlignLeft className="size-4 text-rose-500" />
                        Content
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Paste documentation, FAQs, guides, or any content the bot should reference..."
                          className="min-h-[200px] font-mono text-sm"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <ResponsiveModalFooter className="mt-6">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending && (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  )}
                  Add Document
                </Button>
              </ResponsiveModalFooter>
            </form>
          </Form>
        </TabsContent>

        <TabsContent value="url" className="mt-4">
          <Form {...urlForm}>
            <form onSubmit={urlForm.handleSubmit(onUrlSubmit)}>
              <div className="grid gap-4">
                <FormField
                  control={urlForm.control}
                  name="url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Link2 className="size-4 text-rose-500" />
                        Documentation URL
                        {isScrapingAvailable ? (
                          <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-emerald-500">
                            <span className="relative flex size-2">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                              <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
                            </span>
                            Online
                          </span>
                        ) : (
                          <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-yellow-500">
                            <span className="size-2 rounded-full bg-yellow-500" />
                            Offline
                          </span>
                        )}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="url"
                          placeholder="https://docs.example.com/guide"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        The page will be scraped and indexed in the background.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!isScrapingAvailable && (
                  <div className="flex items-center gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3">
                    <AlertCircle className="size-4 shrink-0 text-yellow-500" />
                    <p className="text-sm text-yellow-600 dark:text-yellow-400">
                      Scraping service is unavailable. Try again later.
                    </p>
                  </div>
                )}
              </div>

              <ResponsiveModalFooter className="mt-6">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={scrapeMutation.isPending || !isScrapingAvailable}
                >
                  {scrapeMutation.isPending && (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  )}
                  Queue URL
                </Button>
              </ResponsiveModalFooter>
            </form>
          </Form>
        </TabsContent>

        <TabsContent value="bulk" className="mt-4">
          {bulkResults ? (
            <div className="space-y-4">
              <div className="max-h-72 space-y-2 overflow-y-auto">
                {bulkResults.map((result, i) => (
                  <div
                    key={i}
                    className="flex min-w-0 items-start gap-2 rounded-lg border p-2.5 text-sm"
                  >
                    {result.status === "queued" ? (
                      <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-500" />
                    ) : (
                      <XCircle className="mt-0.5 size-4 shrink-0 text-red-500" />
                    )}
                    <div className="min-w-0">
                      <p className="truncate font-mono text-xs">{result.url}</p>
                      {result.error && (
                        <p className="text-muted-foreground text-xs">
                          {result.error}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <ResponsiveModalFooter>
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
                <Button
                  onClick={() => {
                    setBulkResults(null);
                    bulkUrlForm.reset();
                  }}
                >
                  Add More
                </Button>
              </ResponsiveModalFooter>
            </div>
          ) : (
            <Form {...bulkUrlForm}>
              <form onSubmit={bulkUrlForm.handleSubmit(onBulkUrlSubmit)}>
                <div className="grid gap-4">
                  <FormField
                    control={bulkUrlForm.control}
                    name="urls"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <List className="size-4 text-rose-500" />
                          URLs
                          {isScrapingAvailable ? (
                            <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-emerald-500">
                              <span className="relative flex size-2">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                                <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
                              </span>
                              Online
                            </span>
                          ) : (
                            <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-yellow-500">
                              <span className="size-2 rounded-full bg-yellow-500" />
                              Offline
                            </span>
                          )}
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={
                              "https://docs.example.com/guide\nhttps://docs.example.com/api\nhttps://docs.example.com/faq"
                            }
                            className="min-h-[160px] font-mono text-sm"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          One URL per line. Up to 25 URLs at a time.
                          {urlCount > 0 && (
                            <span className="ml-1 tabular-nums">
                              ({urlCount} URL{urlCount !== 1 ? "s" : ""})
                            </span>
                          )}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {!isScrapingAvailable && (
                    <div className="flex items-center gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3">
                      <AlertCircle className="size-4 shrink-0 text-yellow-500" />
                      <p className="text-sm text-yellow-600 dark:text-yellow-400">
                        Scraping service is unavailable. Try again later.
                      </p>
                    </div>
                  )}
                </div>

                <ResponsiveModalFooter className="mt-6">
                  <Button type="button" variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      bulkScrapeMutation.isPending || !isScrapingAvailable
                    }
                  >
                    {bulkScrapeMutation.isPending && (
                      <Loader2 className="mr-2 size-4 animate-spin" />
                    )}
                    Queue {urlCount > 0 ? urlCount : ""} URL
                    {urlCount !== 1 ? "s" : ""}
                  </Button>
                </ResponsiveModalFooter>
              </form>
            </Form>
          )}
        </TabsContent>

        <TabsContent value="crawl" className="mt-4">
          <CrawlTab applicationGuildId={applicationGuildId} onClose={onClose} />
        </TabsContent>

        <TabsContent value="official" className="mt-4">
          <OfficialPresetsTab
            applicationGuildId={applicationGuildId}
            onClose={onClose}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

type CrawlState =
  | { phase: "input" }
  | { phase: "discovering"; jobId: string; progress: number; message?: string }
  | { phase: "selection"; urls: string[] }
  | { phase: "error"; error: string };

const crawlFormSchema = z.object({
  url: z.string().url("Please enter a valid URL"),
  maxPages: z.coerce.number().int().min(1).max(150).optional(),
  maxDepth: z.coerce.number().int().min(1).max(10).optional(),
});

type CrawlFormValues = z.infer<typeof crawlFormSchema>;

function CrawlTab({
  applicationGuildId,
  onClose,
}: {
  applicationGuildId: string;
  onClose: () => void;
}) {
  const { data: scrapingStatus } = useScrapingStatus(applicationGuildId);
  const discoverMutation = useDiscoverUrls(applicationGuildId);
  const bulkScrapeMutation = useBulkScrapeUrls(applicationGuildId);
  const { addListener } = useGuildWS(applicationGuildId);

  const [state, setState] = useState<CrawlState>({ phase: "input" });
  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set());
  const [indexedUrls, setIndexedUrls] = useState<Set<string>>(new Set());
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isScrapingAvailable = scrapingStatus?.data?.isAvailable ?? false;

  const applyDiscoveryResult = useCallback(
    (urls: string[], indexedUrlsList: string[]) => {
      const indexed = new Set(indexedUrlsList);
      setIndexedUrls(indexed);
      setState({ phase: "selection", urls });
      setSelectedUrls(new Set(urls.filter((u) => !indexed.has(u))));
    },
    []
  );

  const form = useForm<CrawlFormValues>({
    resolver: zodResolver(crawlFormSchema),
    defaultValues: {
      url: "",
      maxPages: 50,
      maxDepth: 3,
    },
  });

  // Listen for WS events matching our jobId
  useEffect(() => {
    if (state.phase !== "discovering") return;

    const jobId = state.jobId;
    const remove = addListener((msg: WSServerMessage) => {
      if (msg.type === "discover:completed" && msg.jobId === jobId) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        applyDiscoveryResult(msg.urls, msg.indexedUrls);
      } else if (msg.type === "discover:failed" && msg.jobId === jobId) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setState({
          phase: "error",
          error: msg.error ?? "Discovery failed",
        });
      } else if (msg.type === "discover:progress" && msg.jobId === jobId) {
        setState((prev) =>
          prev.phase === "discovering"
            ? { ...prev, progress: msg.progress, message: msg.message }
            : prev
        );
      }
    });

    // Timeout after 3 minutes
    timeoutRef.current = setTimeout(() => {
      setState({
        phase: "error",
        error:
          "Discovery is taking longer than expected. The scraper service may be unavailable.",
      });
    }, 180_000);

    return () => {
      remove();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [
    state.phase,
    state.phase === "discovering" ? state.jobId : null,
    addListener,
    applyDiscoveryResult,
  ]);

  const onSubmit = useCallback(
    async (values: CrawlFormValues) => {
      try {
        const options: { maxPages?: number; maxDepth?: number } = {};
        if (values.maxPages) options.maxPages = values.maxPages;
        if (values.maxDepth) options.maxDepth = values.maxDepth;

        const result = await discoverMutation.mutateAsync({
          url: values.url.trim(),
          options: Object.keys(options).length > 0 ? options : undefined,
        });

        if (result.data.urls) {
          applyDiscoveryResult(result.data.urls, result.data.indexedUrls ?? []);
        } else {
          setState({
            phase: "discovering",
            jobId: result.data.jobId,
            progress: 0,
          });
        }
      } catch {
        setState({
          phase: "error",
          error: "Failed to start URL discovery",
        });
      }
    },
    [discoverMutation, applyDiscoveryResult]
  );

  const onScrapeSelected = useCallback(async () => {
    const urls = [...selectedUrls];
    if (urls.length === 0) return;

    try {
      await bulkScrapeMutation.mutateAsync(urls);
      onClose();
    } catch {
      // Error handled by mutation
    }
  }, [selectedUrls, bulkScrapeMutation, onClose]);

  const toggleUrl = (url: string) => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  const selectableUrls =
    state.phase === "selection"
      ? state.urls.filter((u) => !indexedUrls.has(u))
      : [];

  const toggleAll = () => {
    if (selectedUrls.size === selectableUrls.length) {
      setSelectedUrls(new Set());
    } else {
      setSelectedUrls(new Set(selectableUrls));
    }
  };

  if (state.phase === "discovering") {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="text-muted-foreground mb-3 size-6 animate-spin" />
          <p className="text-sm font-medium">Discovering URLs...</p>
          {state.message && (
            <p className="text-muted-foreground mt-1 text-xs">
              {state.message}
            </p>
          )}
          {state.progress > 0 && (
            <div className="mt-3 h-1.5 w-48 overflow-hidden rounded-full bg-rose-500/10">
              <div
                className="h-full rounded-full bg-rose-500 transition-all duration-300"
                style={{ width: `${state.progress}%` }}
              />
            </div>
          )}
        </div>
        <ResponsiveModalFooter>
          <Button
            variant="outline"
            onClick={() => setState({ phase: "input" })}
          >
            Cancel
          </Button>
        </ResponsiveModalFooter>
      </div>
    );
  }

  if (state.phase === "error") {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="mb-3 size-6 text-red-500" />
          <p className="text-sm font-medium">Discovery Failed</p>
          <p className="text-muted-foreground mt-1 max-w-sm text-pretty text-center text-xs">
            {state.error}
          </p>
        </div>
        <ResponsiveModalFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => setState({ phase: "input" })}>
            Try Again
          </Button>
        </ResponsiveModalFooter>
      </div>
    );
  }

  if (state.phase === "selection") {
    const allIndexed = state.urls.every((u) => indexedUrls.has(u));

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">
            Discovered <span className="tabular-nums">{state.urls.length}</span>{" "}
            URL
            {state.urls.length !== 1 && "s"}
          </p>
          {selectableUrls.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={toggleAll}
            >
              {selectedUrls.size === selectableUrls.length
                ? "Deselect All"
                : "Select All"}
            </Button>
          )}
        </div>

        {allIndexed ? (
          <div className="text-muted-foreground py-8 text-center text-sm">
            All discovered URLs are already indexed.
          </div>
        ) : (
          <div className="-mx-1 max-h-72 space-y-1 overflow-y-auto px-1">
            {state.urls.map((url) => {
              const isIndexed = indexedUrls.has(url);
              return (
                <label
                  key={url}
                  className={`flex min-w-0 items-center gap-2.5 rounded-lg border p-2.5 text-sm transition-colors duration-200 ${
                    isIndexed
                      ? "cursor-default opacity-50"
                      : "cursor-pointer hover:bg-rose-500/[0.04]"
                  }`}
                >
                  <Checkbox
                    checked={selectedUrls.has(url)}
                    onCheckedChange={() => toggleUrl(url)}
                    disabled={isIndexed}
                  />
                  <span className="min-w-0 flex-1 break-all font-mono text-xs">
                    {url}
                  </span>
                  {isIndexed && (
                    <Badge variant="secondary" className="shrink-0 text-[10px]">
                      Indexed
                    </Badge>
                  )}
                </label>
              );
            })}
          </div>
        )}

        <ResponsiveModalFooter>
          <Button
            variant="outline"
            onClick={() => setState({ phase: "input" })}
          >
            Back
          </Button>
          <Button
            disabled={selectedUrls.size === 0 || bulkScrapeMutation.isPending}
            onClick={onScrapeSelected}
          >
            {bulkScrapeMutation.isPending && (
              <Loader2 className="mr-2 size-4 animate-spin" />
            )}
            Scrape <span className="tabular-nums">{selectedUrls.size}</span> URL
            {selectedUrls.size !== 1 && "s"}
          </Button>
        </ResponsiveModalFooter>
      </div>
    );
  }

  // Input phase
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid gap-4">
          <FormField
            control={form.control}
            name="url"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Globe className="size-4 text-rose-500" />
                  Documentation URL
                  {isScrapingAvailable ? (
                    <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-emerald-500">
                      <span className="relative flex size-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
                      </span>
                      Online
                    </span>
                  ) : (
                    <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-yellow-500">
                      <span className="size-2 rounded-full bg-yellow-500" />
                      Offline
                    </span>
                  )}
                </FormLabel>
                <FormControl>
                  <Input
                    type="url"
                    placeholder="https://docs.example.com/"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  All nested pages under this URL will be discovered.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="maxPages"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <List className="size-4 text-rose-500" />
                    Max Pages
                  </FormLabel>
                  <FormControl>
                    <Input type="number" min={1} max={150} {...field} />
                  </FormControl>
                  <FormDescription>
                    Maximum pages to discover (1-150).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="maxDepth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Layers className="size-4 text-rose-500" />
                    Crawl Depth
                  </FormLabel>
                  <FormControl>
                    <Input type="number" min={1} max={10} {...field} />
                  </FormControl>
                  <FormDescription>
                    How many levels deep to follow links (1-10).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {!isScrapingAvailable && (
            <div className="flex items-center gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3">
              <AlertCircle className="size-4 shrink-0 text-yellow-500" />
              <p className="text-sm text-yellow-600 dark:text-yellow-400">
                Scraping service is unavailable. Try again later.
              </p>
            </div>
          )}
        </div>

        <ResponsiveModalFooter className="mt-6">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={discoverMutation.isPending || !isScrapingAvailable}
          >
            {discoverMutation.isPending && (
              <Loader2 className="mr-2 size-4 animate-spin" />
            )}
            Discover URLs
          </Button>
        </ResponsiveModalFooter>
      </form>
    </Form>
  );
}

function extractDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function OfficialPresetsTab({
  applicationGuildId,
  onClose,
}: {
  applicationGuildId: string;
  onClose: () => void;
}) {
  const { data, isPending } = useOfficialPresets(applicationGuildId);
  const applyMutation = useApplyOfficialPreset(applicationGuildId);
  const [confirmPreset, setConfirmPreset] =
    useState<OfficialDocumentPreset | null>(null);

  const presets = data?.data ?? [];
  const applyingPresetId = applyMutation.isPending
    ? (applyMutation.variables as number | undefined)
    : undefined;

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="text-muted-foreground size-5 animate-spin" />
      </div>
    );
  }

  if (presets.length === 0) {
    return (
      <div className="text-muted-foreground py-12 text-center text-sm">
        <BookOpen className="mx-auto mb-2 size-8 opacity-50" />
        <p>No official presets available</p>
      </div>
    );
  }

  if (confirmPreset) {
    const domains = [
      ...new Set(confirmPreset.urls.map((u) => extractDomain(u.url))),
    ];

    return (
      <div className="space-y-4">
        <div>
          <p className="font-medium">Add &quot;{confirmPreset.name}&quot;?</p>
          <p className="text-muted-foreground mt-1 text-pretty text-sm">
            {confirmPreset.urls.length} URL
            {confirmPreset.urls.length !== 1 && "s"} from{" "}
            {domains.slice(0, 3).join(", ")}
            {domains.length > 3 && ` and ${domains.length - 3} more`} will be
            scraped into your knowledge base. Already indexed URLs are skipped.
          </p>
        </div>

        <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border p-2">
          {confirmPreset.urls.map((urlItem) => (
            <div
              key={urlItem.id}
              className="flex items-center gap-2 px-1 py-0.5 text-sm"
            >
              <ExternalLink className="text-muted-foreground size-3 shrink-0" />
              <span className="min-w-0 truncate font-mono text-xs">
                {urlItem.url}
              </span>
            </div>
          ))}
        </div>

        <ResponsiveModalFooter>
          <Button variant="outline" onClick={() => setConfirmPreset(null)}>
            Back
          </Button>
          <Button
            disabled={applyMutation.isPending}
            onClick={() => {
              applyMutation.mutate(confirmPreset.id, {
                onSuccess: () => onClose(),
              });
            }}
          >
            {applyMutation.isPending && (
              <Loader2 className="mr-2 size-4 animate-spin" />
            )}
            Add {confirmPreset.urls.length} URL
            {confirmPreset.urls.length !== 1 && "s"}
          </Button>
        </ResponsiveModalFooter>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="-mx-1 max-h-[320px] min-w-0 space-y-1.5 overflow-y-auto px-1">
        {presets.map((preset) => {
          const isApplying = applyingPresetId === preset.id;

          return (
            <div
              key={preset.id}
              className="flex items-center gap-3 rounded-lg border p-3 transition-colors duration-200 hover:bg-rose-500/[0.04]"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium">{preset.name}</p>
                  <Badge variant="secondary" className="shrink-0 tabular-nums">
                    {preset.urls.length} URL
                    {preset.urls.length !== 1 && "s"}
                  </Badge>
                </div>
                {preset.description && (
                  <p className="text-muted-foreground mt-0.5 text-pretty text-xs">
                    {preset.description}
                  </p>
                )}
              </div>

              <Button
                size="sm"
                variant="outline"
                className="h-7 shrink-0 text-xs"
                disabled={applyMutation.isPending}
                onClick={() => setConfirmPreset(preset)}
              >
                {isApplying ? (
                  <Loader2 className="mr-1 size-3 animate-spin" />
                ) : (
                  <Plus className="mr-1 size-3" />
                )}
                Add
              </Button>
            </div>
          );
        })}
      </div>

      <ResponsiveModalFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </ResponsiveModalFooter>
    </div>
  );
}

AddDocumentModal.contentClassName = "sm:max-w-lg";
AddDocumentModal.displayName = "AddDocumentModal";
